import styled from '@emotion/styled';
import { Flex, Table as ATable, TableProps as ATableProps, theme } from 'antd';
import { useCallback, useMemo, useRef } from 'react';

import { DownIcon, UpIcon } from '../Icon';

/**
 * Styled container for the table with custom scrollbars and styling
 */
const TableContainer = styled('div')`
  .row-active {
    .ant-table-cell {
      background-color: #fedfb9;
    }
  }
  & .ant-table-wrapper {
    & .ant-table {
      border: 1px solid #f0f0f0;
      border-radius: 8px;

      .ant-table-container {
        .ant-table-body,
        .ant-table-content {
          // scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }

        .ant-table-tbody-virtual-holder {
          transition: max-height 0.2s ease;
        }

        .ant-table-thead {
          .ant-table-cell {
            padding: 6.6px 8px;

            .ant-table-column-sorters {
              justify-content: unset;
              display: inline-flex;

              .ant-table-column-title {
                flex: none;
              }
            }
            &:hover {
              // background: #fafafa;
            }
          }
        }
        .ant-table-tbody {
          .ant-table-row {
            height: 32px;
            .ant-table-cell {
              align-content: center;
              padding-top: unset;
              padding-bottom: unset;
            }
            .ant-table-row-expand-icon-cell > button {
              color: #f97316;
            }
          }
          .ant-table-expanded-row-cell {
            padding-left: 0px;
            padding-right: 0px;
          }
          .ant-table-row:not(.ant-table-row-selected) {
            .ant-table-cell {
              &.ant-table-column-sort {
                // background: unset;
              }
            }
          }
        }
      }
      .ant-table-container:has(.ant-table-tbody-virtual-scrollbar-visible) {
        .ant-table-tbody-virtual {
          margin-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }
      }
      .ant-table-tbody-virtual-scrollbar-visible {
        &.ant-table-tbody-virtual-scrollbar-horizontal {
          height: 14px !important;
          bottom: -18px !important;
          // visibility: visible !important;
        }
        &.ant-table-tbody-virtual-scrollbar-vertical {
          width: 14px !important;
        }
      }
    }

    // custom for nested table
    .ant-table-expanded-row {
      .table-expanded {
        .ant-table {
          border: unset;
          margin-inline: unset !important;
          .ant-table-container table > thead > tr:first-child {
            > *:first-child,
            > *:last-child {
              border-start-start-radius: unset;
              border-start-end-radius: unset;
            }
          }
          .ant-table-thead {
            .ant-table-cell {
              padding-top: 4.5px;
              padding-bottom: 4.5px;
              background: #ffedd5;
            }
          }
          .ant-table-tbody {
            .ant-table-cell {
              background: #ffedd580;
            }
          }
        }
      }
    }

    .ant-pagination {
      position: relative;
      padding-left: 210px;

      .ant-pagination-options {
        position: absolute;
        top: 0;
        left: 0;

        &:before {
          content: 'Items per page:';
          padding-right: 10px;
        }
      }
      .ant-pagination-total-text {
        position: absolute;
        top: 0;
        left: 260px;
      }
    }
  }
`;

interface RecordType {
  id: string;
}

interface TableProps<T extends RecordType> extends ATableProps<T> {
  clickRowAble?: boolean;
  onRowSingleClick?: (record: T) => void;
  onRowDoubleClick?: (record: T) => void;
}

/**
 * Enhanced Ant Design Table component with:
 * - Custom styling and scrollbars
 * - Click and double-click row handlers
 * - Custom sort icons
 * - Virtual scrolling support
 * - Nested table styling
 * - Pagination customization
 */
export const Table = <T extends RecordType>({
  clickRowAble = false,
  columns = [],
  pagination = {},
  onRowDoubleClick,
  onRowSingleClick,
  ...props
}: TableProps<T>) => {
  const clickTimeout = useRef<number | null>(null);

  const {
    token: { colorPrimary },
  } = theme.useToken();

  /**
   * Enhance columns with custom sort icons
   */
  const memoizedColumns: TableProps<T>['columns'] = useMemo(() => {
    return columns.map((column) => {
      if (column.sorter) {
        return {
          ...column,
          sortIcon: ({ sortOrder }) => (
            <Flex vertical gap={2} className="ml-2">
              <UpIcon
                color={sortOrder === 'ascend' ? colorPrimary : undefined}
              />
              <DownIcon
                color={sortOrder === 'descend' ? colorPrimary : undefined}
              />
            </Flex>
          ),
        };
      }
      return column;
    });
  }, [colorPrimary, columns]);

  /**
   * Handles row click events with single/double click detection
   * @param record - The row data record
   */
  const handleRowEvents = useCallback(
    (record: T) => {
      if (!clickRowAble) return {};

      return {
        onClick: () => {
          if (clickTimeout.current) {
            clearTimeout(clickTimeout.current);
          }

          clickTimeout.current = window.setTimeout(() => {
            onRowSingleClick?.(record);
            clickTimeout.current = null;
          }, 200);
        },
        onDoubleClick: () => {
          if (clickTimeout.current) {
            clearTimeout(clickTimeout.current);
          }
          onRowDoubleClick?.(record);
        },
      };
    },
    [clickRowAble, onRowSingleClick, onRowDoubleClick]
  );

  return (
    <TableContainer>
      <ATable
        size="middle"
        rootClassName="rect-table"
        columns={memoizedColumns}
        onRow={handleRowEvents}
        pagination={
          pagination && {
            pageSizeOptions: [15, 30, 50, 100], // Default page size options
            ...pagination,
            showTotal: (total) => `in ${total} record(s)`, // Custom total text
          }
        }
        {...props}
      />
    </TableContainer>
  );
};
